/**
 * useProjectMembersForMention — minimal mention-autocomplete data source.
 *
 * Reads `members[]` from the existing `useBoard(projectId)` cache. The
 * board endpoint already returns `{ project, statuses, cards, members }`
 * in one round-trip (see `src/features/board/use-board.ts`), so the
 * mention popover doesn't need a new BE endpoint — it just reuses the
 * cached query.
 *
 * Returns `{ id, name }[]` because that's the minimal shape the TipTap
 * Mention node spec needs: `id` becomes `data-user-id` on the rendered
 * chip and the markdown URL slug, `name` becomes the visible label.
 *
 * If the board hasn't loaded yet (e.g. the user opens a card-detail modal
 * before the board response lands), this returns `[]` — the popover will
 * render an empty list and the user types nothing matches yet.
 */

import { useMemo } from 'react'

import type { UserKind } from '@/api/types-bridge'
import { useBoard } from '@/features/board/use-board'

export interface MentionMember {
  id: string
  name: string
  /**
   * Optional avatar URL. Read defensively from the board members payload
   * — the BE doesn't carry it on `ProjectMember` yet (the schema lands in
   * a follow-up); when it does, the popover lights up automatically.
   */
  avatarUrl?: string | null
  /** Human vs AI agent — the popover row shows an `<AiTag>` for agents. */
  kind?: UserKind
  /**
   * Optional secondary line rendered below the name in the picker —
   * email for real users, "Everyone in this channel" for the @all
   * synthetic entry. Omit to render no second line. Same shape across
   * every mention source (chat / comments / card descriptions).
   */
  subtitle?: string | null
  /**
   * When `'italic'`, the subtitle renders in italic (slightly lighter
   * text color is always applied). Used by the @all synthetic entry to
   * distinguish "this is a description, not a real email/handle".
   * Defaults to plain.
   */
  subtitleStyle?: 'italic' | 'plain'
}

export function useProjectMembersForMention(projectId: string | undefined): MentionMember[] {
  const { data } = useBoard(projectId)

  return useMemo(() => {
    if (!data?.members) return []
    return data.members.map((m) => {
      // Defensive read against an `avatarUrl` field that doesn't yet
      // exist on the typed `ProjectMember`. Casts are safe — the BE may
      // populate it at any time and we want it lit up the moment it does.
      const loose = m as typeof m & { avatarUrl?: string | null }
      return {
        id: m.userId,
        name: m.name,
        avatarUrl: loose.avatarUrl ?? null,
        kind: m.kind,
      }
    })
  }, [data?.members])
}
